import React, { useEffect, useRef, useState } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import styled from '@emotion/styled';
import type { Sources } from 'quill';
//@ts-ignore
import * as Emoji from 'quill-emoji';
//@ts-ignore
import Mention from 'quill-mention';
//@ts-ignore
import AutoLinks from 'quill-auto-links';
import 'react-quill/dist/quill.snow.css';
import 'quill-emoji/dist/quill-emoji.css';
import 'quill-mention/dist/quill.mention.css';
import { borderRadius } from 'react-select/src/theme';

Quill.register({
  'modules/emoji': Emoji,
  'modules/mention': Mention,
  'modules/autoLinks': AutoLinks,
});

const mention = (getMentionValues: () => any[]) => {
  return {
    allowedChars: /^[0-9A-Za-z\sÅÄÖåäö]*$/,
    mentionDenotationChars: ['@'],
    source: function (searchTerm: string, renderList: any, mentionChar: any) {
      const mentionValues = getMentionValues();

      if (searchTerm.length === 0) {
        renderList(mentionValues, searchTerm);
      } else {
        const matches = [];
        for (let i = 0; i < mentionValues.length; i++)
          if (~mentionValues[i].value.toLowerCase().indexOf(searchTerm.toLowerCase()))
            matches.push(mentionValues[i]);
        renderList(matches, searchTerm);
      }
    },
    allowSpaceInQuery: false,
    allowSuggestionsAboveCursor: true,
    positioningStrategy: 'fixed',
  };
};

const Container = styled.div<{
  maxHeight?: number;
  marginBottom?: number;
  borderRadius?: number;
  boxShadow?: string;
  opacity?: string;
}>`
  margin-bottom: ${props => props.marginBottom}px;
  border-radius: ${props => props.borderRadius}px;
  box-shadow: ${props => props.boxShadow};
  opacity: ${props => props.opacity};
  .ql-editor strong {
    font-weight: bold;
  }
  .ql-editor img {
    max-width: 350px !important;
    max-height: 150px !important;
    width: auto;
    height: auto;
    display: block;
  }
  .ql-editor img:hover {
    transform: scale(1.25);
  }
  .ql-container.ql-snow {
    border: none;
  }
  .ql-toolbar.ql-snow {
    border: none;
    border-bottom: 1px solid #ccc;
    margin-right: 20px;
  }
  .ql-container {
    max-height: ${props => (props.maxHeight ? props.maxHeight + 'px' : '122px')};
  }
  .mention {
    background-color: #fff !important;
  }
  #tab-panel .ap {
    margin-top: 16px;
    margin-left: 7px;
  }
  .quill * {
    font-size: 14px;
  }
`;

const toolbarOptions: any = [
  ['bold', 'italic', 'underline', 'strike'], // toggled buttons
  [{ color: [] }, { background: [] }],
  [{ list: 'ordered' }, { list: 'bullet' }],
  ['emoji'],
  ['clean'],
];
//handlers: { emoji: function () {} },

interface PropTypes {
  readonly?: boolean;
  value?: string;
  onChange?: (e: any, source?: Sources) => void;
  onBlur?: (e: any) => void;
  maxheight?: number;
  mentionValues: { id: number; value: string }[];
  setHeightOfContainer?: boolean;
  onKeyDown?: (e: any) => void;
  marginBottom?: number;
  borderRadius?: number;
  boxShadow?: string;
  opacity?: string;
  autoFocus?: boolean;
}

const QuillWysiwyg = (props: PropTypes) => {
  const quillRef = useRef<ReactQuill>(null);
  useEffect(() => {
    if (props.autoFocus) {
      quillRef.current?.focus();
    }
  }, []);
  const mentionValuesRef = useRef(props.mentionValues);
  mentionValuesRef.current = props.mentionValues;
  // Must stay referentially stable: react-quill 2.0.0 regenerates the editor on `modules` change and loses the DOM.
  const modules = React.useMemo(
    () => ({
      toolbar: toolbarOptions,
      'emoji-toolbar': true,
      'emoji-shortname': true,
      mention: mention(() => mentionValuesRef.current ?? []),
      autoLinks: true,
    }),
    []
  );
  return (
    <Container
      maxHeight={props.setHeightOfContainer && props.maxheight}
      marginBottom={props.marginBottom ?? 0}
      borderRadius={props.borderRadius ?? 0}
      boxShadow={props.boxShadow ?? ''}
      opacity={props.opacity ?? ''}
    >
      <ReactQuill
        ref={quillRef}
        modules={modules}
        style={{ height: props.maxheight, fontSize: 16 }}
        theme="snow"
        value={props.value}
        onChange={(content, _delta, source) => {
          props.onChange?.(content, source);
        }}
        preserveWhitespace={true}
        readOnly={props.readonly || false}
        onBlur={(_, __, editor) => {
          props.onBlur && props.onBlur(editor.getHTML());
        }}
        onKeyDown={props.onKeyDown}
      />
    </Container>
  );
};

export default QuillWysiwyg;
